import { useState } from "react";
import { Cake, MessageCircle, PartyPopper, Sparkles, Loader2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useApi, Skeleton, Stagger, Item } from "@/components/portal/ui";
import { AdminTitle } from "@/pages/admin/AdminLayout";

const WEEKS = [[-1, "Minggu Lalu"], [0, "Minggu Ini"], [1, "Minggu Depan"]];

function PreviewDialog({ onClose }) {
  const [name, setName] = useState("Daniel");
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const gen = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/notify/preview-birthday`, { params: { name: name || "Jemaat" } });
      setSamples(data.samples || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Ucapan disalin"); };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="birthday-preview-dialog" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-3xl p-6 shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <div><h2 className="text-lg font-black text-slate-900">Pratinjau Ucapan AI</h2><p className="text-xs text-slate-500">Dibuat otomatis oleh Gemini 3 Flash · variatif tiap kali</p></div>
          </div>
          <button onClick={onClose} data-testid="birthday-preview-close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="mt-5 flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama jemaat" data-testid="birthday-preview-name" className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold outline-none focus:border-blue-500" />
          <button onClick={gen} disabled={loading} data-testid="birthday-preview-generate" className="shrink-0 inline-flex items-center gap-2 bg-blue-600 text-white font-bold rounded-xl px-5 text-sm hover:bg-blue-700 active:scale-95 transition disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Buat
          </button>
        </div>
        {samples.length === 0 && !loading && <p className="mt-6 text-center text-sm text-slate-400" data-testid="birthday-preview-empty">Tekan "Buat" untuk melihat 3 contoh ucapan yang akan dikirim ke jemaat.</p>}
        {loading && <div className="mt-6 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        <div className="mt-5 space-y-3" data-testid="birthday-preview-samples">
          {samples.map((s, i) => (
            <div key={i} className="bg-slate-50 border border-slate-100 rounded-2xl p-4" data-testid={`birthday-preview-sample-${i}`}>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{s}</p>
              <button onClick={() => copy(s)} className="mt-2 text-[11px] font-bold text-blue-600 hover:text-blue-700">Salin</button>
            </div>
          ))}
        </div>
        {samples.length > 0 && (
          <button onClick={gen} disabled={loading} data-testid="birthday-preview-regenerate" className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-slate-600 bg-slate-100 rounded-xl py-2.5 hover:bg-slate-200 active:scale-95 transition"><RefreshCw className="w-4 h-4" />Buat variasi lain</button>
        )}
        <p className="mt-4 text-[11px] text-slate-400">Ucapan dikirim otomatis via notifikasi & WhatsApp pada pagi hari ulang tahun jemaat. Kalimat selalu dibuat berbeda agar tidak monoton.</p>
      </div>
    </div>
  );
}

function waLink(p) {
  const num = (p.phone || "").replace(/\D/g, "").replace(/^0/, "62");
  const text = `Selamat ulang tahun ke-${p.age}, ${p.name}! Tuhan memberkati dan melindungi engkau. — Tim Pastoral GIM Church`;
  return num ? `https://wa.me/${num}?text=${encodeURIComponent(text)}` : null;
}

export default function Birthdays() {
  const [week, setWeek] = useState(0);
  const [preview, setPreview] = useState(false);
  const { data } = useApi(`/admin/birthdays?week=${week}`);
  return (
    <div data-testid="admin-birthdays">
      <AdminTitle eyebrow="Tim Pastoral" title="Ulang Tahun Jemaat">
        <button onClick={() => setPreview(true)} data-testid="birthday-preview-open" className="inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:brightness-105 active:scale-95 transition shadow-sm"><Sparkles className="w-4 h-4" />Pratinjau Ucapan AI</button>
        {WEEKS.map(([v, l]) => (
          <button key={v} onClick={() => setWeek(v)} data-testid={`birthday-week-${v}`} className={`text-sm rounded-full px-4 py-2 transition active:scale-95 ${week === v ? "bg-blue-600 text-white font-bold" : "bg-white border border-slate-200 text-slate-600 font-semibold"}`}>{l}</button>
        ))}
      </AdminTitle>
      {preview && <PreviewDialog onClose={() => setPreview(false)} />}
      {!data ? <Skeleton className="h-64" /> : (
        <>
          <p className="text-sm text-slate-500 mb-5" data-testid="birthday-range">{fmtDate(data.start, { day: "numeric", month: "long" })} – {fmtDate(data.end)} · {data.items.length} jemaat</p>
          {data.items.length === 0 && <div className="bg-white rounded-3xl p-10 text-center border border-slate-100 text-slate-500" data-testid="birthday-empty"><Cake className="w-8 h-8 mx-auto text-slate-300" /><p className="mt-3">Tidak ada jemaat berulang tahun di minggu ini.</p></div>}
          <Stagger className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.items.map((p) => {
              const wa = waLink(p);
              return (
                <Item key={p.user_id + p.birthday} className={`rounded-3xl p-6 border shadow-sm hover:-translate-y-1 transition duration-300 ${p.is_today ? "bg-gradient-to-br from-indigo-600 via-blue-900 to-slate-900 text-white border-transparent" : "bg-white border-slate-100"}`} data-testid={`birthday-${p.user_id}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-[11px] font-bold rounded-full px-3 py-1 ${p.is_today ? "bg-white/20 border border-white/30" : "bg-blue-100 text-blue-700"}`}>{p.is_today ? "Hari ini" : fmtDate(p.birthday, { weekday: "long", day: "numeric", month: "short" })}</span>
                    {p.is_today ? <PartyPopper className="w-5 h-5 text-amber-300" /> : <Cake className="w-5 h-5 text-slate-300" />}
                  </div>
                  <p className="mt-5 text-xl font-extrabold">{p.name}</p>
                  <p className={`text-sm ${p.is_today ? "text-white/70" : "text-slate-500"}`}>Genap {p.age} tahun · NIG {p.nig || "—"}</p>
                  {wa ? (
                    <a href={wa} target="_blank" rel="noreferrer" data-testid={`birthday-wa-${p.user_id}`} className="mt-5 inline-flex items-center gap-2 bg-emerald-500 text-white font-bold rounded-xl px-4 py-2.5 text-sm hover:bg-emerald-600 active:scale-95 transition"><MessageCircle className="w-4 h-4" />Kirim Ucapan WA</a>
                  ) : <p className={`mt-5 text-xs ${p.is_today ? "text-white/50" : "text-slate-400"}`}>No. WhatsApp belum diisi</p>}
                </Item>
              );
            })}
          </Stagger>
        </>
      )}
    </div>
  );
}
